import { Capacitor } from '@capacitor/core';
import { sqliteStorage } from './sqliteStorage';
import { offlineStorage } from './offlineStorage';

class UnifiedStorage {
  private useSQLite: boolean = false;

  async init(): Promise<void> {
    this.useSQLite = await this.shouldUseSQLite();
    
    if (this.useSQLite) {
      console.log('[UnifiedStorage] Using SQLite');
      await sqliteStorage.init();
    } else {
      console.log('[UnifiedStorage] Using IndexedDB');
      // IndexedDB is auto-initialized on first use
    }
  }

  private async shouldUseSQLite(): Promise<boolean> {
    const platform = Capacitor.getPlatform();
    return platform === 'android' || platform === 'ios';
  }

  // Orders
  async saveOrder(order: any): Promise<void> {
    if (this.useSQLite) {
      await sqliteStorage.saveOrder(order);
    } else {
      await offlineStorage.saveOrder(order);
    }
  }

  async getOrders(): Promise<any[]> {
    if (this.useSQLite) {
      return sqliteStorage.getOrders();
    } else {
      return offlineStorage.getOrders();
    }
  }

  async getOrder(id: string): Promise<any | null> {
    if (this.useSQLite) {
      return sqliteStorage.getOrder(id);
    } else {
      return offlineStorage.getOrder(id);
    }
  }

  async updateOrderSyncStatus(id: string, syncStatus: 'synced' | 'conflict'): Promise<void> {
    if (this.useSQLite) {
      await sqliteStorage.updateOrderSyncStatus(id, syncStatus);
    } else {
      await offlineStorage.updateOrderSyncStatus(id, syncStatus);
    }
  }

  async getPendingOrders(): Promise<any[]> {
    if (this.useSQLite) {
      return sqliteStorage.getPendingOrders();
    } else {
      return offlineStorage.getPendingOrders();
    }
  }

  // Customers
  async saveCustomer(customer: any): Promise<void> {
    if (this.useSQLite) {
      await sqliteStorage.saveCustomer(customer);
    } else {
      await offlineStorage.saveCustomer(customer);
    }
  }

  async getCustomers(): Promise<any[]> {
    if (this.useSQLite) {
      return sqliteStorage.getCustomers();
    } else {
      return offlineStorage.getCustomers();
    }
  }

  async getCustomer(id: string): Promise<any | null> {
    if (this.useSQLite) {
      return sqliteStorage.getCustomer(id);
    } else {
      return offlineStorage.getCustomer(id);
    }
  }

  async updateCustomerSyncStatus(id: string, syncStatus: 'synced' | 'conflict'): Promise<void> {
    if (this.useSQLite) {
      await sqliteStorage.updateCustomerSyncStatus(id, syncStatus);
    } else {
      await offlineStorage.updateCustomerSyncStatus(id, syncStatus);
    }
  }

  async getPendingCustomers(): Promise<any[]> {
    if (this.useSQLite) {
      return sqliteStorage.getPendingCustomers();
    } else {
      return offlineStorage.getPendingCustomers();
    }
  }

  // Services (read-only cache)
  async saveServices(services: any[]): Promise<void> {
    if (this.useSQLite) {
      await sqliteStorage.saveServices(services);
    } else {
      await offlineStorage.saveServices(services);
    }
  }

  async getServices(): Promise<any[]> {
    if (this.useSQLite) {
      return sqliteStorage.getServices();
    } else {
      return offlineStorage.getServices();
    }
  }

  // Inventory (read-only cache)
  async saveInventory(items: any[]): Promise<void> {
    if (this.useSQLite) {
      await sqliteStorage.saveInventory(items);
    } else {
      await offlineStorage.saveInventory(items);
    }
  }

  async getInventory(): Promise<any[]> {
    if (this.useSQLite) {
      return sqliteStorage.getInventory();
    } else {
      return offlineStorage.getInventory();
    }
  }

  // Sync Queue
  async addToSyncQueue(item: { id: string; method: 'POST' | 'PUT' | 'DELETE'; endpoint: string; data: any }): Promise<void> {
    if (this.useSQLite) {
      await sqliteStorage.addToSyncQueue(item);
    } else {
      await offlineStorage.addToSyncQueue(item);
    }
  }

  async getSyncQueue(): Promise<any[]> {
    if (this.useSQLite) {
      return sqliteStorage.getSyncQueue();
    } else {
      return offlineStorage.getSyncQueue();
    }
  }

  async removeFromSyncQueue(id: string): Promise<void> {
    if (this.useSQLite) {
      await sqliteStorage.removeFromSyncQueue(id);
    } else {
      await offlineStorage.removeFromSyncQueue(id);
    }
  }

  async incrementSyncQueueRetries(id: string): Promise<void> {
    if (this.useSQLite) {
      await sqliteStorage.incrementSyncQueueRetries(id);
    } else {
      await offlineStorage.incrementSyncQueueRetries(id);
    }
  }

  // Clear all data
  async clearAll(): Promise<void> {
    if (this.useSQLite) {
      await sqliteStorage.clearAll();
    } else {
      await offlineStorage.clearAll();
    }
  }

  // Check if running on native platform
  async isNative(): Promise<boolean> {
    return this.useSQLite;
  }
}

export const unifiedStorage = new UnifiedStorage();
